import { get, writable } from 'svelte/store';
import { location, querystring, replace } from 'svelte-spa-router';

import { afterLoginParams, user_data } from 'src/stores';
import {
	buildAlexaAppLinkingHref,
	normalizeAlexaQuery,
	rewriteIntegrationsAlexaHref,
	shouldPreserveAlexaDeepLink,
} from './alexa_app_linking_helpers';

/**
 * Query string Android / iOS last asked the webview to open on the Alexa
 * linking page. Kept until that page is actually showing those params so a
 * landing-route race cannot drop the deep link.
 */
export const pendingAlexaQuery = writable<string | null>(null);

/**
 * Open the Alexa linking route with the native query. Persists the query so
 * layout init can re-apply it if a `/` landing redirect races the navigation.
 */
export function navigateToAlexaAppLinking(params: unknown): void {
	const query = normalizeAlexaQuery(params);
	pendingAlexaQuery.set(query);

	if (!get(user_data).email) {
		afterLoginParams.set({
			route: '/settings/app-to-app-alexa',
			location: '/settings/app-to-app-alexa',
			querystring: query,
			params: {},
		});
		return;
	}

	replace(buildAlexaAppLinkingHref(query));
}

/**
 * Re-apply a persisted Alexa query when the user is logged in and not already
 * on that URL. Returns true when a replace was issued.
 */
export function applyPendingAlexaDeepLink(): boolean {
	const query = get(pendingAlexaQuery);
	if (query === null) {
		return false;
	}

	if (!get(user_data).email) {
		return false;
	}

	const href = buildAlexaAppLinkingHref(query);
	const currentLoc = get(location) || '';
	const currentQs = normalizeAlexaQuery(get(querystring) || '');

	if (currentLoc.includes('app-to-app-alexa') && currentQs === query) {
		return false;
	}

	replace(href);
	return true;
}

/**
 * Layout-router hook: rewrite the integrations alias and skip the `/` landing
 * redirect while an Alexa deep link is pending. Returns true when the caller
 * must not send the user to the home/dashboard landing.
 */
export function handleAlexaMenuRoute(
	route: string,
	currentLocation: string,
	currentQuery: string,
): boolean {
	const rewritten = rewriteIntegrationsAlexaHref(route, currentQuery);
	if (rewritten) {
		replace(rewritten);
		return true;
	}

	if (currentLocation === '/' || route === '/') {
		if (
			shouldPreserveAlexaDeepLink(
				currentLocation,
				get(pendingAlexaQuery),
				typeof window !== 'undefined' ? window.location.hash : '',
			)
		) {
			applyPendingAlexaDeepLink();
			return true;
		}
	}

	return false;
}
